<div class="form edit-role-connections">
    <div class="role-connections">
        <div class="px-1 pb-3 d-flex flex-column gap-1">
            <div
                 [adaptTooltip]="hasIncompleteRoleConnections ? 'Please select a ' + model?.singularName + ' above before adding another new one' : ''">
                <button adaptButton="addLink"
                        [disabled]="hasIncompleteRoleConnections"
                        (click)="promiseToAddRoleConnection()">Add {{ model?.singularName?.toLowerCase() }}</button>
            </div>

            @for (addedRoleConnection of addedRoleConnections; track addedRoleConnection.roleConnectionId) {
                <adapt-edit-role-connection [minStartDate]="connection?.startDate"
                                            [roleConnection]="addedRoleConnection"
                                            [model]="model"
                                            [isAdding]="true"
                                            (roleConnectionChanged)="onRoleConnectionChanged($event)"
                                            (roleConnectionDeleted)="onRoleConnectionDeletion($event)"></adapt-edit-role-connection>
            }
        </div>
    </div>
</div>

<!-- repaintChangesOnly is needed,
otherwise changing Start Date in the edit dialog causes the menu to destroy,
which means the event won't fire as its not being listened to anymore -->
<dx-data-grid class="role-connection-grid"
              [dataSource]="entity?.roleConnections ?? []"
              keyExpr="roleConnectionId"
              [columnAutoWidth]="true"
              [allowColumnResizing]="true"
              [allowColumnReordering]="true"
              [rowAlternationEnabled]="true"
              [hoverStateEnabled]="true"
              [showRowLines]="true"
              [showBorders]="true"
              [repaintChangesOnly]="true"
              (onInitialized)="onGridInitialised($event)"
              (onContentReady)="onGridContentReady($event)"
              (onRowExpanded)="onGridRowExpanded($event)"
              (onRowCollapsed)="onGridRowCollapsed($event)">
    <dxo-filter-row [visible]="true"> </dxo-filter-row>
    <dxo-header-filter [visible]="true"> </dxo-header-filter>
    <dxo-group-panel [visible]="false"> </dxo-group-panel>
    <dxo-paging [pageSize]="25"
                [enabled]="false"> </dxo-paging>
    <dxo-pager [showPageSizeSelector]="true"
               [allowedPageSizes]="[10, 25, 50, 100, 250]"> </dxo-pager>
    <dxo-scrolling [useNative]="false"> </dxo-scrolling>
    <dxo-column-chooser [enabled]="false"> </dxo-column-chooser>
    <dxo-load-panel [enabled]="true"> </dxo-load-panel>

    <dxi-sort-by-group-summary-info summaryItem="custom"></dxi-sort-by-group-summary-info>
    <dxo-summary [calculateCustomSummary]="calcCustomSummary">
        <dxi-group-item summaryType="custom"
                        name="activeStateCustomSummary"
                        showInColumn="Type"></dxi-group-item>
    </dxo-summary>

    <!-- COLUMNS -->
    @if (role) {
        <dxi-column caption="Full name"
                    dataField="connection.person.fullName"
                    dataType="string"
                    name="People"
                    sortOrder="asc"
                    cellTemplate="fullNameCellTemplate"> </dxi-column>
    }

    @if (connection) {
        <dxi-column caption="Role"
                    dataField="role.label"
                    dataType="string"
                    name="Role"
                    sortOrder="asc"
                    cellTemplate="roleCellTemplate"> </dxi-column>
    }

    <dxi-column caption="Date"
                dataType="date"
                dataField="startDate"
                [format]="ShortDate"
                [allowFiltering]="true"
                [allowSorting]="true"
                cellTemplate="dateCellTemplate"
                [width]="240"> </dxi-column>

    <dxi-column caption="End date"
                dataType="date"
                dataField="endDate"
                [format]="ShortDate"
                [allowFiltering]="true"
                [allowSorting]="true"
                [visible]="false"> </dxi-column>

    <dxi-column caption="Status"
                dataField="status"
                [calculateCellValue]="calculateActiveCellValue"
                dataType="string"
                [allowSorting]="true"
                [allowFiltering]="true"
                [showEditorAlways]="false"
                [editorOptions]="{ disabled: true }"
                [groupIndex]="0"
                groupCellTemplate="statusGroupCellTemplate"
                [width]="120"> </dxi-column>

    <dxi-column caption=""
                dataType="string"
                dataField="actions"
                cellTemplate="actionCellTemplate"
                [allowSorting]="false"
                [allowReordering]="false"
                [allowHiding]="false"
                [allowFiltering]="false"
                [allowResizing]="false"
                [fixed]="true"
                fixedPosition="right"
                [width]="50"> </dxi-column>
    <!-- END COLUMNS -->

    <!-- TEMPLATES -->
    <div *dxTemplate="let item of 'fullNameCellTemplate'"
         class="d-flex flex-wrap gap-1 align-items-center">
        <adapt-link-person class="me-auto"
                           [person]="item.data.connection?.person"
                           [showImage]="true"
                           imageSize="xs"></adapt-link-person>
        <adapt-display-role-connection-handover-status [roleConnection]="item.data"></adapt-display-role-connection-handover-status>
        @if (item.data?.entityAspect.entityState.isAddedModifiedOrDeleted()) {
            <i adaptTooltip="Unsaved changes"
               class="fas fa-fw fa-circle text-warning"></i>
        }
    </div>

    <div *dxTemplate="let item of 'roleCellTemplate'"
         class="d-flex flex-wrap gap-1 align-items-center">
        <adapt-link-role class="me-auto"
                         [role]="item.data.role"></adapt-link-role>
        <adapt-display-role-connection-handover-status [roleConnection]="item.data"></adapt-display-role-connection-handover-status>
        @if (item.data?.entityAspect.entityState.isAddedModifiedOrDeleted()) {
            <i adaptTooltip="Unsaved changes"
               class="fas fa-fw fa-circle text-warning"></i>
        }
    </div>

    <div *dxTemplate="let item of 'dateCellTemplate'">
        {{ item.data.startDate | adaptDate }}
        @if (item.data.endDate) {
            - {{ item.data.endDate | adaptDate }}
        }
    </div>

    <div *dxTemplate="let item of 'actionCellTemplate'">
        <!-- disallow editing of a new row -->
        @if (item.data && !addedRoleConnections.has(item.data)) {
            <adapt-role-connection-actions [roleConnection]="item.data"
                                           (roleConnectionChanged)="onRoleConnectionChanged($event)"
                                           [model]="model"
                                           data-test="role-connection-actions"></adapt-role-connection-actions>
        }
    </div>

    <div *dxTemplate="let item of 'statusGroupCellTemplate'">
        {{ item.value }}
    </div>
    <!-- END TEMPLATES -->
</dx-data-grid>
